import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeTempId,
  buildLocalizedDetails,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getProductDisplayTitle,
} from "./utils";
import swal from "sweetalert";
import { getMenuLanguages } from "../../../../../constants/menuLanguages";
import {
  formatDualMoney,
  getCurrencySymbol,
  getRestaurantCurrencyCode,
  getRestaurantSecondaryCurrencyCode,
  parseOptionalPrice,
} from "../../../../../utils/currency";
import CurrencyFlag from "../../../AppsMenu/AppProfile/CurrencyFlag";
import { AutoConvertButton } from "../../../Dashboard/DualPriceFields";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import ProductBadgesEditor from "../../../shared/ProductBadgesEditor";
import NutritionValuesEditor from "../../../shared/NutritionValuesEditor";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../../../utils/imageValidation";
import { fileToDataUrl } from "../draftStorage";
import {
  getProductBadgeOption,
  normalizeProductBadgesForSave,
} from "../../../../../constants/productBadgeOptions";
import {
  getNutritionOption,
  normalizeNutritionValuesForSave,
} from "../../../../../constants/nutritionOptions";
import {
  getZoneCountryFlagUrl,
  getZoneCountryLabel,
} from "../../../../../constants/zoneCountryOptions";

function getBadgeChipLabel(type, value) {
  if (type === "customized" && value) return value;
  if (type === "zone" && value) return getZoneCountryLabel(value, "en");
  return getProductBadgeOption(type).label;
}

function ProductRowExtras({ product }) {
  const { t } = useTranslation();
  const badges = normalizeProductBadgesForSave(product?.productBadges);
  const nutrition = normalizeNutritionValuesForSave(product?.nutritionValues);
  if (!badges.length && !nutrition.length) return null;

  return (
    <div
      className={`start-products-row__extras${
        badges.length && nutrition.length ? " has-both" : ""
      }`}
    >
      {badges.length > 0 ? (
        <div className="start-products-row__extra-col">
          <span className="start-products-row__extra-label">{t("wizardProductBadgesLabel")}</span>
          <div className="start-products-row__chips">
            {badges.map((item) => (
              <span key={`badge-${item.type}`} className="start-products-row__chip">
                {item.type === "zone" && item.value ? (
                  <img
                    src={getZoneCountryFlagUrl(item.value)}
                    alt=""
                    className="start-products-row__chip-flag"
                  />
                ) : (
                  <span className="start-products-row__chip-icon" aria-hidden="true">
                    {getProductBadgeOption(item.type).icon}
                  </span>
                )}
                <span>{getBadgeChipLabel(item.type, item.value)}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {nutrition.length > 0 ? (
        <div className="start-products-row__extra-col">
          <span className="start-products-row__extra-label">{t("wizardProductNutritionLabel")}</span>
          <div className="start-products-row__chips">
            {nutrition.map((item) => {
              const option = getNutritionOption(item.type);
              return (
                <span key={`nutrition-${item.type}`} className="start-products-row__chip is-nutrition">
                  <span className="start-products-row__chip-icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <span>
                    {option.label}
                    {item.value ? ` · ${item.value}` : ""}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const ExtraPanel = ({ open, onToggle, title, meta, children }) => (
  <div className={`start-step1-extra${open ? " is-open" : ""}`}>
    <button
      type="button"
      className="start-step1-extra__toggle"
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="start-step1-extra__chevron" aria-hidden="true">
        <i className="fa fa-chevron-down" />
      </span>
      <span className="start-step1-extra__title">{title}</span>
      {meta ? <span className="start-step1-extra__meta">{meta}</span> : null}
    </button>
    {open ? <div className="start-step1-extra__body">{children}</div> : null}
  </div>
);

function productToFormState(product) {
  const titles = {};
  const descriptions = {};
  (product?.productDetails || []).forEach((detail) => {
    if (!detail?.language) return;
    titles[detail.language] = detail.productTitle || "";
    descriptions[detail.language] = detail.productDescription || "";
  });
  return {
    titles,
    descriptions,
    price: product?.price != null && product?.price !== "" ? String(product.price) : "",
    secondaryPrice:
      product?.secondaryPrice != null && product?.secondaryPrice !== ""
        ? String(product.secondaryPrice)
        : "",
    productBadges: Array.isArray(product?.productBadges) ? product.productBadges : [],
    nutritionValues: Array.isArray(product?.nutritionValues) ? product.nutritionValues : [],
  };
}

function stripProductFromRelations(relations, productTempId) {
  const next = { ...(relations || {}) };

  if (next.productChoices?.[productTempId]) {
    delete next.productChoices[productTempId];
  }

  if (next.categoryProducts) {
    next.categoryProducts = Object.fromEntries(
      Object.entries(next.categoryProducts).map(([categoryId, ids]) => [
        categoryId,
        (ids || []).filter((id) => id !== productTempId),
      ])
    );
  }

  if (next.subcategoryProducts) {
    next.subcategoryProducts = Object.fromEntries(
      Object.entries(next.subcategoryProducts).map(([subcategoryId, ids]) => [
        subcategoryId,
        (ids || []).filter((id) => id !== productTempId),
      ])
    );
  }

  return next;
}

const Step2Products = ({
  draft,
  setDraft,
  goNext,
  goPrev,
  productPhotos,
  setProductPhotos,
}) => {
  const { t } = useTranslation();
  const allLanguages = getMenuLanguages(t);
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const restaurantCurrency = getRestaurantCurrencyCode();
  const secondaryCurrency = getRestaurantSecondaryCurrencyCode();
  const currencySymbol = getCurrencySymbol(restaurantCurrency);
  const photoInputRef = useRef(null);

  const [titles, setTitles] = useState({});
  const [descriptions, setDescriptions] = useState({});
  const [price, setPrice] = useState("");
  const [secondaryPrice, setSecondaryPrice] = useState("");
  const [badges, setBadges] = useState([]);
  const [nutrition, setNutrition] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [addedOpen, setAddedOpen] = useState(false);
  const createProductsSectionRef = useRef(null);

  const primaryLangMeta = allLanguages.find((lang) => lang.code === menuPrimaryLanguage);
  const primaryTitle = titles[menuPrimaryLanguage] || "";
  const primaryDescription = descriptions[menuPrimaryLanguage] || "";

  const updateTitle = (language, value) => {
    setTitles((prev) => ({ ...prev, [language]: value }));
  };

  const updateDescription = (language, value) => {
    setDescriptions((prev) => ({ ...prev, [language]: value }));
  };

  const resetProductForm = () => {
    setTitles({});
    setDescriptions({});
    setPrice("");
    setSecondaryPrice("");
    setBadges([]);
    setNutrition([]);
    setPhotoPreview(null);
    setEditingProductId(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const canSave = useMemo(() => primaryTitle.trim().length > 0, [primaryTitle]);

  useEffect(() => {
    if (!editingProductId || !createProductsSectionRef.current) return;

    createProductsSectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    const titleInput = document.getElementById("wizard-product-title");
    titleInput?.focus({ preventScroll: true });
  }, [editingProductId]);

  const applyPhotoForProduct = (tempId) => {
    setProductPhotos((prev) => {
      const next = { ...(prev || {}) };
      if (photoPreview) {
        next[tempId] = photoPreview;
      } else {
        delete next[tempId];
      }
      return next;
    });
  };

  const handlePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const { isValid, message } = validateImageFile(file, 5);
    if (!isValid) {
      swal(t("warning"), message, "warning");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setPhotoPreview(dataUrl);
    } catch {
      swal(t("warning"), t("wizardProductPhotoUploadError"), "warning");
    }
    event.target.value = "";
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const saveProduct = () => {
    if (!canSave) {
      showWizardValidation(t, "wizardProductTitleRequired");
      return;
    }

    const productDetails = buildLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles,
      descriptions,
    });

    const productPayload = {
      price: Number(price || 0),
      secondaryPrice: parseOptionalPrice(secondaryPrice),
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      productDetails,
      productBadges: badges,
      nutritionValues: nutrition,
    };

    if (editingProductId) {
      setDraft((prev) => ({
        ...prev,
        products: (prev.products || []).map((product) =>
          product.tempId === editingProductId
            ? { ...product, ...productPayload }
            : product
        ),
      }));
      applyPhotoForProduct(editingProductId);
      showWizardToast(t, "wizardProductUpdated");
    } else {
      const tempId = makeTempId("product");
      setDraft((prev) => ({
        ...prev,
        products: [
          ...(Array.isArray(prev.products) ? prev.products : []),
          {
            tempId,
            ...productPayload,
          },
        ],
      }));
      applyPhotoForProduct(tempId);
      showWizardToast(t, "wizardProductAdded");
    }

    resetProductForm();
  };

  const startEditProduct = (product) => {
    const formState = productToFormState(product);
    setEditingProductId(product.tempId);
    setTitles(formState.titles);
    setDescriptions(formState.descriptions);
    setPrice(formState.price);
    setSecondaryPrice(formState.secondaryPrice);
    setBadges(formState.productBadges);
    setNutrition(formState.nutritionValues);
    setPhotoPreview(productPhotos?.[product.tempId] || null);
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const cancelEdit = () => {
    resetProductForm();
  };

  const removeProduct = async (productTempId) => {
    const confirmed = await swal({
      title: t("areYouSure"),
      text: t("wizardDeleteProductConfirm"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    if (editingProductId === productTempId) {
      resetProductForm();
    }

    setDraft((prev) => ({
      ...prev,
      products: (prev.products || []).filter((product) => product.tempId !== productTempId),
      relations: stripProductFromRelations(prev.relations, productTempId),
    }));
    showWizardToast(t, "wizardProductDeleted");
  };

  const products = Array.isArray(draft.products) ? draft.products : [];
  const canNext = products.length > 0;

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero start-step-hero-scroll-target" ref={createProductsSectionRef}>
          <h2 className="start-step-title">
            <span className="step-badge">3</span>
            {t("wizardCreateProductsHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardCreateProductsSub")}</p>
        </div>

        <div className="start-products">
          <section className="start-products-form" aria-label={t("wizardCreateProductsHeading")}>
            <div className="start-products-identity">
              <div className="start-products-photo-wrap">
                <button
                  type="button"
                  className={`start-products-photo${photoPreview ? " has-photo" : ""}`}
                  onClick={() => photoInputRef.current?.click()}
                  aria-label={photoPreview ? t("change") : t("wizardAddLogo")}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="start-products-photo__img" />
                  ) : (
                    <>
                      <span className="start-products-photo__icon" aria-hidden="true">
                        <i className="fa fa-camera" />
                      </span>
                      <span className="start-products-photo__label">{t("wizardAddLogo")}</span>
                    </>
                  )}
                </button>
                {photoPreview ? (
                  <button
                    type="button"
                    className="start-products-photo__remove"
                    onClick={removePhoto}
                    aria-label={t("remove")}
                    title={t("remove")}
                  >
                    <i className="fa fa-times" aria-hidden="true" />
                  </button>
                ) : null}
                <input
                  ref={photoInputRef}
                  type="file"
                  hidden
                  accept={IMAGE_PICKER_ACCEPT}
                  onChange={handlePhotoSelect}
                />
                <span className="start-products-photo__meta">{t("optional")}</span>
              </div>

              <div className="start-products-identity__fields">
                <div>
                  <label className="start-step1-label" htmlFor="wizard-product-title">
                    {t("wizardProductTitleLabel")}
                    <span className="start-step1-required" aria-hidden="true">*</span>
                    {primaryLangMeta ? (
                      <span className="start-step1-label__lang">{primaryLangMeta.label}</span>
                    ) : null}
                  </label>
                  <input
                    id="wizard-product-title"
                    className="form-control start-input"
                    placeholder={t("placeholderProductTitleExample")}
                    value={primaryTitle}
                    onChange={(e) => updateTitle(menuPrimaryLanguage, e.target.value)}
                  />
                </div>

                <div className={`start-products-prices${secondaryCurrency ? " has-secondary" : ""}`}>
                  <div className="start-products-form__price">
                    <label className="start-step1-label" htmlFor="wizard-product-price">
                      {t("price")}
                      <span className="start-step1-label__lang">
                        <CurrencyFlag code={restaurantCurrency} className="currency-flag--inline" />
                        {restaurantCurrency}
                      </span>
                    </label>
                    <div className="input-group start-price-input-group">
                      <span className="input-group-text start-price-input-prefix">{currencySymbol}</span>
                      <input
                        id="wizard-product-price"
                        className="form-control start-input start-price-input"
                        type="number"
                        placeholder={t("wizardPricePlaceholder")}
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                      />
                    </div>
                  </div>

                  {secondaryCurrency ? (
                    <>
                      <div className="start-products-prices__convert">
                        <AutoConvertButton
                          compact
                          amount={price}
                          fromCode={restaurantCurrency}
                          toCode={secondaryCurrency}
                          onConverted={setSecondaryPrice}
                        />
                      </div>
                      <div className="start-products-form__price">
                        <label className="start-step1-label" htmlFor="wizard-product-secondary-price">
                          {t("secondaryPrice")}
                          <span className="start-step1-label__lang">
                            <CurrencyFlag code={secondaryCurrency} className="currency-flag--inline" />
                            {secondaryCurrency}
                          </span>
                          <span className="start-step1-label__lang">{t("optional")}</span>
                        </label>
                        <div className="input-group start-price-input-group">
                          <span className="input-group-text start-price-input-prefix">
                            {getCurrencySymbol(secondaryCurrency)}
                          </span>
                          <input
                            id="wizard-product-secondary-price"
                            className="form-control start-input start-price-input"
                            type="number"
                            placeholder={t("secondaryPricePlaceholder")}
                            value={secondaryPrice}
                            onChange={(e) => setSecondaryPrice(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <label className="start-step1-label" htmlFor="wizard-product-description">
                {t("wizardProductDescriptionLabel")}
                {primaryLangMeta ? (
                  <span className="start-step1-label__lang">{primaryLangMeta.label}</span>
                ) : null}
                <span className="start-step1-label__lang">{t("optional")}</span>
              </label>
              <textarea
                id="wizard-product-description"
                className="form-control start-textarea start-textarea--compact"
                rows={2}
                placeholder={t("placeholderProductDescriptionExample")}
                value={primaryDescription}
                onChange={(e) => updateDescription(menuPrimaryLanguage, e.target.value)}
              />
            </div>

            {menuOptionalLanguages.length > 0 ? (
              <ExtraPanel
                open={languagesOpen}
                onToggle={() => setLanguagesOpen((v) => !v)}
                title={t("wizardOptionalLanguagesLabel")}
                meta={t("optional")}
              >
                <p className="start-step1-extra__hint">{t("wizardProductOptionalLanguagesFromStep1")}</p>
                <div className="start-optional-titles">
                  {menuOptionalLanguages.map((code) => {
                    const lang = allLanguages.find((item) => item.code === code);
                    if (!lang) return null;
                    return (
                      <div key={code} className="start-optional-title-row">
                        <label className="start-step1-label" htmlFor={`wizard-product-title-${code}`}>
                          {t("wizardProductTitleLabel")}
                          <span className="start-step1-label__lang">{lang.label}</span>
                        </label>
                        <input
                          id={`wizard-product-title-${code}`}
                          type="text"
                          className="form-control start-input start-input--compact"
                          placeholder={t("placeholderProductTitleExample")}
                          value={titles[code] || ""}
                          onChange={(e) => updateTitle(code, e.target.value)}
                        />
                        <label
                          className="start-step1-label start-step1-label--spaced"
                          htmlFor={`wizard-product-desc-${code}`}
                        >
                          {t("wizardProductDescriptionLabel")}
                          <span className="start-step1-label__lang">{lang.label}</span>
                        </label>
                        <textarea
                          id={`wizard-product-desc-${code}`}
                          className="form-control start-textarea start-textarea--compact"
                          rows={2}
                          placeholder={t("placeholderProductDescriptionExample")}
                          value={descriptions[code] || ""}
                          onChange={(e) => updateDescription(code, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </ExtraPanel>
            ) : null}

            <div className="start-products-extras">
              <ProductBadgesEditor values={badges} onChange={setBadges} />
              <NutritionValuesEditor values={nutrition} onChange={setNutrition} />
            </div>

            <div className="start-products-form__actions">
              <button
                className="start-products-add"
                disabled={!canSave}
                onClick={saveProduct}
                type="button"
              >
                {editingProductId ? t("wizardUpdateProduct") : t("wizardAddProduct")}
              </button>
              {editingProductId ? (
                <button className="start-static-btn" onClick={cancelEdit} type="button">
                  {t("cancel")}
                </button>
              ) : null}
            </div>
          </section>

          <section
            className={`start-products-added${addedOpen ? " is-open" : ""}`}
            aria-label={t("wizardProductsAdded")}
          >
            <button
              type="button"
              className="start-products-added__toggle"
              onClick={() => setAddedOpen((open) => !open)}
              aria-expanded={addedOpen}
            >
              <h3 className="start-products-added__title">{t("wizardProductsAdded")}</h3>
              <span className="start-products-added__count">{products.length}</span>
              <span className="start-products-added__chevron" aria-hidden="true">
                <i className="fa fa-chevron-down" />
              </span>
            </button>
            <div className="start-products-added__body" hidden={!addedOpen}>
                {products.length === 0 ? (
                  <p className="start-products-empty">{t("wizardNoProductsYet")}</p>
                ) : (
                  <ul className="start-products-rows">
                    {products.map((product) => {
                      const thumb = productPhotos?.[product.tempId] || null;
                      return (
                        <li
                          key={product.tempId}
                          className={`start-products-row${
                            editingProductId === product.tempId ? " is-editing" : ""
                          }`}
                        >
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              className="start-products-row__thumb"
                            />
                          ) : (
                            <span className="start-products-row__thumb start-products-row__thumb--empty" aria-hidden="true">
                              <i className="fa fa-image" />
                            </span>
                          )}
                          <div className="start-products-row__body">
                            <div className="start-products-row__header">
                              <div className="start-products-row__main">
                                <span className="start-products-row__title">
                                  {getProductDisplayTitle(product, menuPrimaryLanguage)}
                                </span>
                                <span className="start-products-row__price">
                                  {formatDualMoney(product.price ?? 0, product.secondaryPrice)}
                                </span>
                              </div>
                              <div className="start-products-row__actions">
                                <button
                                  className="start-products-row__icon-btn is-edit"
                                  onClick={() => startEditProduct(product)}
                                  type="button"
                                  aria-label={t("edit")}
                                  title={t("edit")}
                                >
                                  <i className="fa fa-pen" aria-hidden="true" />
                                </button>
                                <button
                                  className="start-products-row__icon-btn is-danger"
                                  onClick={() => removeProduct(product.tempId)}
                                  type="button"
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-trash-alt" aria-hidden="true" />
                                </button>
                              </div>
                            </div>
                            <ProductRowExtras product={product} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep3of9")}</div>
        <button
          className={`btn btn-primary sw-btn-next ms-1 start-btn-next${!canNext ? " disabled" : ""}`}
          disabled={!canNext}
          onClick={goNext}
          type="button"
        >
          {t("wizardContinue")} <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
};

export default Step2Products;
